import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActions, Button,
  Chip, TextField, InputAdornment, Tabs, Tab, IconButton, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Autocomplete,
  RadioGroup, Radio, FormControlLabel, FormLabel, Snackbar, Avatar, Switch,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CampaignIcon from '@mui/icons-material/Campaign';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';
import { CONTENT_TEMPLATES, renderTemplateBody } from '../../data/contentTemplates';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const TYPE_OPTIONS = [
  { key: 'brochure', label: 'Brochure', icon: PictureAsPdfIcon, color: '#1B365D' },
  { key: 'poster', label: 'Poster', icon: ImageIcon, color: '#7B1FA2' },
  { key: 'guide', label: 'Guide', icon: DescriptionIcon, color: '#00B894' },
  { key: 'banner', label: 'Banner', icon: ImageIcon, color: '#E65100' },
  { key: 'template', label: 'Template', icon: DescriptionIcon, color: '#546E7A' },
  { key: 'handbook', label: 'Handbook', icon: DescriptionIcon, color: '#1565C0' },
];

const STATUS_COLORS = {
  draft: { bg: '#ECEFF1', color: '#546E7A', label: 'Draft' },
  published: { bg: '#E8F5E9', color: '#2E7D32', label: 'Published' },
  expired: { bg: '#FFEBEE', color: '#C62828', label: 'Expired' },
};

const blankForm = {
  name: '',
  type: 'brochure',
  product: '',
  content: '',
  imageUrl: null,
  audienceMode: 'all', // 'all' | 'specific' | 'role'
  audienceUserIds: [],
  audienceRoles: [],
  startDate: new Date().toISOString().slice(0, 10),
  endDate: '',
  status: 'draft',
};

const AVAILABLE_AUDIENCE_ROLES = [
  { key: 'free_user', label: 'Free User', color: '#00B894' },
  { key: 'individual_agent', label: 'Individual Agent', color: '#7B1FA2' },
  { key: 'partner', label: 'Partner', color: '#1B365D' },
];

export default function MarketingMaterials() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editForm, setEditForm] = useState(blankForm);
  const [editingId, setEditingId] = useState(null);
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });
  const bump = () => setRefreshKey((k) => k + 1);

  // Live data
  const allUsers = useMemo(() => db.users.getAll(), [refreshKey]);
  const eligibleAudienceUsers = useMemo(
    () => allUsers.filter((u) => ['partner', 'individual_agent', 'free_user'].includes(u.role)),
    [allUsers]
  );
  const products = useMemo(() => db.products.getAll(), [refreshKey]);
  const productNameById = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.id] = p.name; });
    return m;
  }, [products]);

  const materials = useMemo(() => db.marketingMaterials.getAllEnriched(), [refreshKey]);

  const types = useMemo(() => ['All', ...TYPE_OPTIONS.map((t) => t.label)], []);

  const filtered = useMemo(() => {
    return materials.filter((m) => {
      const typeLabel = TYPE_OPTIONS.find((t) => t.key === m.type)?.label || m.type;
      if (tab > 0 && typeLabel !== types[tab]) return false;
      if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [materials, tab, search, types]);

  // ─── Handlers ────────────────────────────────────────────
  const openCreateDialog = () => {
    setEditingId(null);
    setEditForm(blankForm);
    setEditDialogOpen(true);
  };

  const openEditDialog = (m) => {
    setEditingId(m.id);
    // Determine audience mode from the existing data
    let audienceMode = 'all';
    if (m.audienceUserIds !== '*' && Array.isArray(m.audienceUserIds) && m.audienceUserIds.length > 0) {
      audienceMode = 'specific';
    } else if (Array.isArray(m.audienceRoles) && m.audienceRoles.length > 0) {
      audienceMode = 'role';
    }
    setEditForm({
      name: m.name || '',
      type: m.type || 'brochure',
      product: m.product || '',
      content: m.content || '',
      imageUrl: m.imageUrl || null,
      audienceMode,
      audienceUserIds: Array.isArray(m.audienceUserIds) ? m.audienceUserIds : [],
      audienceRoles: Array.isArray(m.audienceRoles) ? m.audienceRoles : [],
      startDate: m.startDate || new Date().toISOString().slice(0, 10),
      endDate: m.endDate || '',
      status: m.status === 'expired' ? 'published' : (m.status || 'draft'),
    });
    setEditDialogOpen(true);
  };

  // Read uploaded image file as a data URL
  const handleImageUpload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showSnack('Image must be under 5 MB', 'warning');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setEditForm((f) => ({ ...f, imageUrl: evt.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!editForm.name.trim()) {
      showSnack('Name is required', 'warning');
      return;
    }
    // Resolve audience from the selected mode
    let audienceUserIds = '*';
    let audienceRoles = [];
    if (editForm.audienceMode === 'specific') {
      audienceUserIds = editForm.audienceUserIds;
      audienceRoles = [];
    } else if (editForm.audienceMode === 'role') {
      audienceUserIds = [];
      audienceRoles = editForm.audienceRoles;
    }

    const payload = {
      name: editForm.name.trim(),
      type: editForm.type,
      product: editForm.product || null,
      content: editForm.content,
      imageUrl: editForm.imageUrl,
      audienceUserIds,
      audienceRoles,
      startDate: editForm.startDate || null,
      endDate: editForm.endDate || null,
      status: editForm.status,
      createdBy: user?.id || 'SYSTEM',
    };
    if (editingId) {
      db.marketingMaterials.update(editingId, payload);
      db.auditLogs.create({
        userId: user?.id, userName: user?.name, userRole: user?.role,
        action: 'update_material', entityType: 'marketing_material',
        entityId: editingId, description: `Updated material "${payload.name}"`,
      });
      showSnack('Material updated');
    } else {
      const created = db.marketingMaterials.create(payload);
      db.auditLogs.create({
        userId: user?.id, userName: user?.name, userRole: user?.role,
        action: 'create_material', entityType: 'marketing_material',
        entityId: created.id, description: `Created material "${created.name}"`,
      });
      showSnack('Material created');
    }
    setEditDialogOpen(false);
    bump();
  };

  const handleDuplicate = (m) => {
    const copy = db.marketingMaterials.create({
      name: `${m.name} (Copy)`,
      type: m.type,
      product: m.product,
      content: m.content,
      imageUrl: m.imageUrl,
      audienceUserIds: m.audienceUserIds,
      audienceRoles: m.audienceRoles,
      startDate: m.startDate,
      endDate: m.endDate,
      status: 'draft',
      createdBy: user?.id || 'SYSTEM',
    });
    showSnack(`Duplicated as "${copy.name}"`);
    bump();
  };

  const handleDelete = (id) => {
    db.marketingMaterials.delete(id);
    db.auditLogs.create({
      userId: user?.id, userName: user?.name, userRole: user?.role,
      action: 'delete_material', entityType: 'marketing_material',
      entityId: id, description: 'Deleted marketing material',
    });
    setDeleteConfirmId(null);
    showSnack('Material deleted', 'warning');
    bump();
  };

  const openPreview = (m) => {
    setPreviewMaterial(m);
    setPreviewDialogOpen(true);
  };

  const insertTemplate = (templateKey) => {
    const tpl = CONTENT_TEMPLATES.find((t) => t.key === templateKey);
    if (tpl) {
      setEditForm((f) => ({ ...f, content: tpl.body, type: tpl.type || f.type }));
    }
  };

  // ─── Render ──────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>Marketing Materials</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage marketing materials for partners and agents
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small" placeholder="Search materials..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
            sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY, fontWeight: 600 }}>
            Create Material
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        {types.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* Grid */}
      {filtered.length > 0 ? (
        <Grid container spacing={3}>
          {filtered.map((item) => {
            const typeMeta = TYPE_OPTIONS.find((t) => t.key === item.type) || TYPE_OPTIONS[0];
            const TypeIcon = typeMeta.icon;
            const statusMeta = STATUS_COLORS[item.computedStatus] || STATUS_COLORS.draft;
            // Resolve audience label + color
            let audienceLabel;
            let audienceChipBg = '#FFF8E1';
            let audienceChipColor = '#F57F17';
            if (item.audienceUserIds === '*') {
              audienceLabel = 'All Users';
              audienceChipBg = '#E8F5E9';
              audienceChipColor = '#2E7D32';
            } else if (Array.isArray(item.audienceRoles) && item.audienceRoles.length > 0) {
              audienceLabel = `${item.audienceRoles.length} role${item.audienceRoles.length > 1 ? 's' : ''}`;
              audienceChipBg = '#E3F2FD';
              audienceChipColor = '#1565C0';
            } else if (Array.isArray(item.audienceUserIds) && item.audienceUserIds.length > 0) {
              audienceLabel = `${item.audienceUserIds.length} user${item.audienceUserIds.length !== 1 ? 's' : ''}`;
            } else {
              audienceLabel = 'No audience';
              audienceChipBg = '#FFEBEE';
              audienceChipColor = '#C62828';
            }
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card sx={{
                  borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }, transition: 'all 0.2s',
                }}>
                  {/* Preview banner — show uploaded image or type icon fallback */}
                  <Box sx={{
                    height: 160, backgroundColor: item.imageUrl ? '#000' : `${typeMeta.color}10`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderBottom: '1px solid #E0E0E0', position: 'relative', overflow: 'hidden',
                  }}>
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <TypeIcon sx={{ fontSize: 44, color: typeMeta.color, opacity: 0.6 }} />
                    )}
                    <Chip label={statusMeta.label} size="small"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        backgroundColor: statusMeta.bg, color: statusMeta.color,
                        fontWeight: 700, fontSize: '0.65rem',
                      }} />
                  </Box>
                  <CardContent sx={{ p: 2.5, flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Chip label={typeMeta.label} size="small"
                        sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#E3F2FD', color: '#1565C0' }} />
                      <Chip label={audienceLabel} size="small"
                        sx={{
                          fontSize: '0.65rem', height: 20,
                          backgroundColor: audienceChipBg,
                          color: audienceChipColor,
                        }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {item.product ? (productNameById[item.product] || item.product) : 'All Products'}
                    </Typography>
                    {(item.startDate || item.endDate) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {item.startDate || '—'} → {item.endDate || '∞'}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        ⬇ {item.downloadCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ↗ {item.shareCount || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2.5, pb: 2, pt: 0, gap: 0.5 }}>
                    <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => openEditDialog(item)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem', flex: 1 }}>
                      Edit
                    </Button>
                    <Tooltip title="Preview">
                      <IconButton size="small" onClick={() => openPreview(item)}>
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton size="small" onClick={() => handleDuplicate(item)}>
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteConfirmId(item.id)}>
                        <DeleteIcon sx={{ fontSize: 18, color: '#C62828' }} />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No marketing materials. Click <strong>Create Material</strong> to get started.
        </Alert>
      )}

      {/* Edit / Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
            {editingId ? 'Edit Marketing Material' : 'Create Marketing Material'}
          </Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            Configure content, audience, and promotion period
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Material Name *"
                value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth select size="small" label="Type"
                value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}>
                {TYPE_OPTIONS.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select size="small" label="Product"
                value={editForm.product} onChange={(e) => setEditForm({ ...editForm, product: e.target.value })}>
                <MenuItem value="">All Products</MenuItem>
                {products.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Promotional image upload */}
            <Grid item xs={12}>
              <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, display: 'block' }}>
                Promotional Image (optional)
              </FormLabel>
              {editForm.imageUrl ? (
                <Box sx={{
                  position: 'relative', borderRadius: 2, overflow: 'hidden',
                  border: '1px solid #E0E0E0',
                }}>
                  <img
                    src={editForm.imageUrl}
                    alt="Preview"
                    style={{ width: '100%', maxHeight: 240, objectFit: 'contain', display: 'block', backgroundColor: '#F5F7FA' }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, p: 1, backgroundColor: '#F5F7FA', borderTop: '1px solid #E0E0E0' }}>
                    <Button size="small" component="label"
                      sx={{ textTransform: 'none', borderRadius: 2 }}>
                      Replace
                      <input type="file" hidden accept="image/*"
                        onChange={(e) => handleImageUpload(e.target.files?.[0])} />
                    </Button>
                    <Button size="small" color="error"
                      onClick={() => setEditForm((f) => ({ ...f, imageUrl: null }))}
                      sx={{ textTransform: 'none', borderRadius: 2 }}>
                      Remove
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed #C4CDD5', borderRadius: 2, p: 3, textAlign: 'center',
                    backgroundColor: '#FAFBFC',
                    '&:hover': { borderColor: PRIMARY, backgroundColor: '#F0F4F8' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Upload a promotional image (poster, banner, etc.)
                  </Typography>
                  <Button variant="outlined" size="small" component="label"
                    sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Choose Image
                    <input type="file" hidden accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files?.[0])} />
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    JPG, PNG, WebP up to 5 MB
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Content + template insert */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Content Template</FormLabel>
                <TextField select size="small" label="Insert from template"
                  value="" onChange={(e) => insertTemplate(e.target.value)}
                  sx={{ minWidth: 240 }}>
                  <MenuItem value="">— Select a template —</MenuItem>
                  {CONTENT_TEMPLATES.map((t) => (
                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField fullWidth multiline rows={8}
                placeholder="Write the content body here. Use {{REFERRAL_LINK}} as a placeholder — when partners share this material, it'll be replaced with their actual referral URL."
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                helperText="Tip: Use {{REFERRAL_LINK}} placeholder for automatic partner link substitution"
                sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </Grid>

            {/* Audience */}
            <Grid item xs={12}>
              <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Who can use this material? *</FormLabel>
              <RadioGroup row value={editForm.audienceMode}
                onChange={(e) => setEditForm({ ...editForm, audienceMode: e.target.value })}>
                <FormControlLabel value="all" control={<Radio />} label="All Users" />
                <FormControlLabel value="role" control={<Radio />} label="By Role" />
                <FormControlLabel value="specific" control={<Radio />} label="Specific Users" />
              </RadioGroup>

              {editForm.audienceMode === 'role' && (
                <Box sx={{ mt: 1, p: 2, backgroundColor: '#F5F7FA', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    Select which roles can see this material
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {AVAILABLE_AUDIENCE_ROLES.map((r) => {
                      const selected = editForm.audienceRoles.includes(r.key);
                      return (
                        <Chip
                          key={r.key}
                          label={r.label}
                          onClick={() => {
                            setEditForm((f) => ({
                              ...f,
                              audienceRoles: selected
                                ? f.audienceRoles.filter((x) => x !== r.key)
                                : [...f.audienceRoles, r.key],
                            }));
                          }}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: selected ? r.color : '#E0E0E0',
                            color: selected ? 'white' : '#333',
                            '&:hover': {
                              backgroundColor: selected ? r.color : '#BDBDBD',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                  {editForm.audienceRoles.length === 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      Select at least one role
                    </Typography>
                  )}
                </Box>
              )}

              {editForm.audienceMode === 'specific' && (
                <Autocomplete
                  multiple size="small" sx={{ mt: 1 }}
                  options={eligibleAudienceUsers}
                  getOptionLabel={(opt) => `${opt.name} (${opt.id})`}
                  value={eligibleAudienceUsers.filter((u) => editForm.audienceUserIds.includes(u.id))}
                  onChange={(_, newVal) => setEditForm({ ...editForm, audienceUserIds: newVal.map((u) => u.id) })}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Select users"
                      placeholder={editForm.audienceUserIds.length === 0 ? 'Add users...' : ''} />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, index) => (
                      <Chip variant="outlined" size="small" label={opt.name} {...getTagProps({ index })}
                        sx={{
                          backgroundColor:
                            opt.role === 'partner' ? '#E3F2FD' :
                            opt.role === 'individual_agent' ? '#F3E5F5' : '#E8F5E9',
                          color:
                            opt.role === 'partner' ? '#1565C0' :
                            opt.role === 'individual_agent' ? '#7B1FA2' : '#2E7D32',
                        }} />
                    ))
                  }
                />
              )}
            </Grid>

            {/* Promotion period */}
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Promotion Start Date"
                value={editForm.startDate} onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Promotion End Date"
                value={editForm.endDate} onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#F5F7FA', borderRadius: 2 }}>
                <FormControlLabel
                  control={<Switch checked={editForm.status === 'published'}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.checked ? 'published' : 'draft' })} />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {editForm.status === 'published' ? 'Published' : 'Draft'}
                  </Typography>}
                />
                <Typography variant="caption" color="text.secondary">
                  {editForm.status === 'published'
                    ? 'Material is live and visible to assigned partners/agents'
                    : 'Draft is hidden from partners until published'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: PRIMARY }}>
            {editingId ? 'Save Changes' : 'Create Material'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Material Preview</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {previewMaterial?.name}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewMaterial && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={TYPE_OPTIONS.find((t) => t.key === previewMaterial.type)?.label || previewMaterial.type}
                  size="small" sx={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }} />
                <Chip label={STATUS_COLORS[previewMaterial.computedStatus]?.label} size="small"
                  sx={{
                    backgroundColor: STATUS_COLORS[previewMaterial.computedStatus]?.bg,
                    color: STATUS_COLORS[previewMaterial.computedStatus]?.color, fontWeight: 600,
                  }} />
              </Box>
              {previewMaterial.imageUrl && (
                <Box sx={{ mb: 2, textAlign: 'center', backgroundColor: '#000', borderRadius: 2, overflow: 'hidden' }}>
                  <img
                    src={previewMaterial.imageUrl}
                    alt={previewMaterial.name}
                    style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
                  />
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.secondary">Content (with sample referral link)</Typography>
              <Box sx={{
                mt: 1, p: 2, backgroundColor: '#F5F7FA', borderRadius: 2,
                whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem',
                maxHeight: 400, overflowY: 'auto',
              }}>
                {renderTemplateBody(previewMaterial.content || '(no content)', 'https://imoney.my/ref/SAMPLE')}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this material?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This action cannot be undone. Partners with this material assigned will no longer see it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirmId)}
            sx={{ borderRadius: 2, textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
